// Phase 3b-2: TanStack Query bindings for the protest safe lane. RPCs live on
// zou's ProjectDataService (OpenProtestWatch, UpdateProtestWatch,
// ListProtestWatches, AddPublicProtestDecision, ListPublicProtestDecisions),
// proxied through sabaody. OpenProtestWatch computes the 10-day awareness
// window server-side. user_id/company_id are filled server-side from the JWT.
import { Timestamp } from '@bufbuild/protobuf';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { projectDataClient } from '../../lib/connect/clients';
const WATCH_KEY = 'protest-watches';
const DECISION_KEY = 'protest-decisions';
/** Load a project's protest watches for the timeline list. */ export function useProtestWatches(projectId) {
    return useQuery({
        queryKey: [
            WATCH_KEY,
            projectId
        ],
        queryFn: async ()=>{
            const resp = await projectDataClient.listProtestWatches({
                projectId
            });
            return resp.protestWatches ?? [];
        },
        enabled: !!projectId
    });
}
/** Load the PUBLIC GAO decisions attached to a watch. */ export function useProtestDecisions(watchId) {
    return useQuery({
        queryKey: [
            DECISION_KEY,
            watchId
        ],
        queryFn: async ()=>{
            const resp = await projectDataClient.listPublicProtestDecisions({
                protestWatchId: watchId
            });
            return resp.decisions ?? [];
        },
        enabled: !!watchId
    });
}
function useProtestMutation(fn, failMsg) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: fn,
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    WATCH_KEY
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    DECISION_KEY
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : failMsg);
        }
    });
}
export function useOpenProtestWatch() {
    return useProtestMutation((args)=>projectDataClient.openProtestWatch({
            projectId: args.projectId,
            awardNoticeDate: args.awardNoticeDate ? Timestamp.fromDate(args.awardNoticeDate) : undefined,
            debriefDate: args.debriefDate ? Timestamp.fromDate(args.debriefDate) : undefined,
            protestLikelihood: args.protestLikelihood,
            competitorPosture: args.competitorPosture ?? '',
            deliveryImpact: args.deliveryImpact ?? '',
            notes: args.notes ?? ''
        }), 'Failed to open protest watch');
}
export function useUpdateProtestWatch() {
    return useProtestMutation((args)=>projectDataClient.updateProtestWatch({
            protestWatchId: args.protestWatchId,
            status: args.status ?? '',
            protestLikelihood: args.protestLikelihood ?? ''
        }), 'Failed to update protest watch');
}
export function useAddPublicProtestDecision() {
    return useProtestMutation((args)=>projectDataClient.addPublicProtestDecision({
            protestWatchId: args.protestWatchId,
            docketNumber: args.docketNumber,
            agency: args.agency ?? '',
            outcome: args.outcome ?? '',
            correctiveActionPattern: args.correctiveActionPattern ?? '',
            publicSummary: args.publicSummary ?? ''
        }), 'Failed to add public protest decision');
}
export const PROTEST_LIKELIHOODS = [
    'low',
    'medium',
    'high'
];
export const PROTEST_STATUSES = [
    'watching',
    'window_open',
    'window_closed',
    'protested',
    'cleared'
];
